/**
 * MANAGEMENT (15) — ANALYTICS data layer. The eight cross-lab analytics aggregation reads behind the
 * flagship Analytics section. Code-verified against server/src/modules/management/analytics.routes.js —
 * every route rides the `platformRoute` chain and is gated by `management.analytics.read`:
 *
 *   GET /management/analytics/revenue          → RevenueAnalytics
 *   GET /management/analytics/subscriptions    → SubscriptionsAnalytics
 *   GET /management/analytics/wilayas          → WilayasAnalytics
 *   GET /management/analytics/referrals        → ReferralsAnalytics
 *   GET /management/analytics/support          → SupportAnalytics
 *   GET /management/analytics/modules          → ModulesAnalytics
 *   GET /management/analytics/analyzers        → AnalyzersAnalytics
 *   GET /management/analytics/analysis-types   → AnalysisTypesAnalytics
 *
 * CROSS-LAB, NOT lab-scoped — the keys live under the shared `["platform","management","analytics",…]`
 * namespace (a lab switch never invalidates them; one operator session reads the whole estate), mirroring
 * the sibling `metricsKeys` + `managementKeys` factories. Reads follow the established convention: gate
 * first (`enabled = useIsPlatformAdmin() && online`), then branch on data. A cloud-only route 404/501 on a
 * local box is surfaced via the shared `isManagementAbsent` (re-exported from ./api). The `wilayas`
 * endpoint takes no period (a pure point-in-time group), so its hook has no period argument.
 */
import { useApiQuery } from "@/api";
import type { AnalyticsPeriod } from "./analytics.types";
import type {
  AnalysisTypesAnalytics,
  AnalyzersAnalytics,
  ModulesAnalytics,
  ReferralsAnalytics,
  RevenueAnalytics,
  SubscriptionsAnalytics,
  SupportAnalytics,
  WilayasAnalytics,
} from "./analytics.types";

/** How long the ranking/top-N reads stay warm — analytics move slowly relative to a triage screen. */
const ANALYTICS_STALE_MS = 60_000;

/** The analytics query keys — rooted under the platform/management/analytics namespace, NOT lab-scoped. */
export const analyticsKeys = {
  root: ["platform", "management", "analytics"] as const,
  revenue: (period: AnalyticsPeriod) =>
    ["platform", "management", "analytics", "revenue", period] as const,
  subscriptions: (period: AnalyticsPeriod) =>
    ["platform", "management", "analytics", "subscriptions", period] as const,
  wilayas: () => ["platform", "management", "analytics", "wilayas"] as const,
  referrals: (period: AnalyticsPeriod) =>
    ["platform", "management", "analytics", "referrals", period] as const,
  support: (period: AnalyticsPeriod) =>
    ["platform", "management", "analytics", "support", period] as const,
  modules: (period: AnalyticsPeriod, limit: number) =>
    ["platform", "management", "analytics", "modules", period, limit] as const,
  analyzers: (period: AnalyticsPeriod, limit: number) =>
    ["platform", "management", "analytics", "analyzers", period, limit] as const,
  analysisTypes: (period: AnalyticsPeriod, limit: number) =>
    ["platform", "management", "analytics", "analysis-types", period, limit] as const,
};

/** GET /management/analytics/revenue — MRR run-rate (plan prices) + settled-cash total/series. */
export function useRevenueAnalytics(period: AnalyticsPeriod, enabled: boolean) {
  return useApiQuery<RevenueAnalytics>(analyticsKeys.revenue(period), "/management/analytics/revenue", {
    query: { period },
    enabled,
    staleTime: ANALYTICS_STALE_MS,
  });
}

/** GET /management/analytics/subscriptions — per-plan × per-statut drill-down. */
export function useSubscriptionsAnalytics(period: AnalyticsPeriod, enabled: boolean) {
  return useApiQuery<SubscriptionsAnalytics>(
    analyticsKeys.subscriptions(period),
    "/management/analytics/subscriptions",
    { query: { period }, enabled, staleTime: ANALYTICS_STALE_MS },
  );
}

/** GET /management/analytics/wilayas — per-Wilaya active-lab distribution cohorts (period-free). */
export function useWilayasAnalytics(enabled: boolean) {
  return useApiQuery<WilayasAnalytics>(analyticsKeys.wilayas(), "/management/analytics/wilayas", {
    enabled,
    staleTime: ANALYTICS_STALE_MS,
  });
}

/** GET /management/analytics/referrals — lab↔doctor links, free-vs-paid, demand levels, referral %. */
export function useReferralsAnalytics(period: AnalyticsPeriod, enabled: boolean) {
  return useApiQuery<ReferralsAnalytics>(
    analyticsKeys.referrals(period),
    "/management/analytics/referrals",
    { query: { period }, enabled, staleTime: ANALYTICS_STALE_MS },
  );
}

/** GET /management/analytics/support — SupportTicket.categorie breakdown + trend. */
export function useSupportAnalytics(period: AnalyticsPeriod, enabled: boolean) {
  return useApiQuery<SupportAnalytics>(analyticsKeys.support(period), "/management/analytics/support", {
    query: { period },
    enabled,
    staleTime: ANALYTICS_STALE_MS,
  });
}

/** GET /management/analytics/modules — most-requested modules/routes from RequestLog, top-N. */
export function useModulesAnalytics(period: AnalyticsPeriod, limit: number, enabled: boolean) {
  return useApiQuery<ModulesAnalytics>(
    analyticsKeys.modules(period, limit),
    "/management/analytics/modules",
    { query: { period, limit }, enabled, staleTime: ANALYTICS_STALE_MS },
  );
}

/** GET /management/analytics/analyzers — most-used analyzers from AnalyzerHealthMetric, top-N. */
export function useAnalyzersAnalytics(period: AnalyticsPeriod, limit: number, enabled: boolean) {
  return useApiQuery<AnalyzersAnalytics>(
    analyticsKeys.analyzers(period, limit),
    "/management/analytics/analyzers",
    { query: { period, limit }, enabled, staleTime: ANALYTICS_STALE_MS },
  );
}

/** GET /management/analytics/analysis-types — most-in-demand analysis parameters, top-N. */
export function useAnalysisTypesAnalytics(period: AnalyticsPeriod, limit: number, enabled: boolean) {
  return useApiQuery<AnalysisTypesAnalytics>(
    analyticsKeys.analysisTypes(period, limit),
    "/management/analytics/analysis-types",
    { query: { period, limit }, enabled, staleTime: ANALYTICS_STALE_MS },
  );
}
